import { Fragment, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import algosdk from "algosdk";

import { Transition } from "@headlessui/react";
import {
	addNewAccountInUserProfile,
	addNewAccountSecretKeyForUser,
	checkAccountAvailabilityInUserProfile,
	getAccountIDByNFD,
	getNFDByAccountID,
} from "actions/accounts";
import useTheme from "hooks/useTheme";
import PasteIcon from "theme/icons/Paste";
import KeyboardIcon from "theme/icons/Keyboard";
import ArrowFrontIcon from "theme/icons/ArrowFront";
import PasteFromClipBoardModal from "theme/modals/existingAccount/PasteFromClipBoardModal";
import EnterUsingKeyboardModal from "theme/modals/existingAccount/EnterUsingKeyboardModal";
import UserIconRoundIcon from "theme/icons/UserIconRound";
import { convertBase64 } from "helpers/functions";
import EditIcon from "theme/icons/Edit";
import useNotification from "hooks/useNotification";

const ImportTAccount = ({ navigatePage }) => {
	const dispatch = useDispatch();
	const { appLanguage, themeColor } = useTheme();
	const { notify } = useNotification();

	const hiddenImageFileInput = useRef();

	const updatingData = useSelector((state) => state.UpdatingData);

	const [mnemonic, setMnemonic] = useState(null);
	const [recoveredAccount, setRecoveredAccount] = useState(null);
	const [isAccountVerified, setIsAccountVerified] = useState(false);

	const [accountName, setAccountName] = useState(null);
	const [accountImage, setAccountImage] = useState(null);

	const [pasteFromClipBoardModalIsOpen, setPasteFromClipBoardModalIsOpen] = useState(false);
	const [enterUsingKeyboardModalIsOpen, setEnterUsingKeyboardModalIsOpen] = useState(false);

	const [isWarningGiven, setIsWarningGiven] = useState(false);

	const PasteFromClipboard = async () => {
		const clipboardMnemonic = await navigator.clipboard.readText();

		setMnemonic(clipboardMnemonic.split(" "));
	};

	const MnemonicToArray = (e, index) => {
		if (mnemonic) {
			const temp = [...mnemonic];
			temp[index] = e.target.value;

			setMnemonic(temp);
		} else if (!mnemonic) setMnemonic(e.target.value.split(" "));
	};

	const handleFileRead = async (e) => {
		const file = e.target.files[0];
		const base64 = await convertBase64(file);

		setAccountImage(base64);
	};

	const RecoverAccount = async () => {
		let account = null;

		try {
			account = algosdk.mnemonicToSecretKey(mnemonic.join(" "));
		} catch (error) {
			if (!isWarningGiven) {
				setIsWarningGiven(true);
				notify("error", "There is an error in the Secret Words you entered, please verify before resubmitting");
			} else ClearAllStateAndValues();
		}

		if (account?.addr && account?.sk) {
			const isAccountAvailable = await checkAccountAvailabilityInUserProfile({
				"account-address": account.addr,
			});

			if (!isAccountAvailable) notify("error", "Account already imported");
			else {
				setIsAccountVerified(true);
				setPasteFromClipBoardModalIsOpen(false);
				setEnterUsingKeyboardModalIsOpen(false);

				const accountNFD = await getNFDByAccountID(account.addr);
				if (accountNFD) {
					setAccountName(accountNFD);

					await getAccountIDByNFD(accountNFD).then((data) => {
						if (data?.properties) {
							if (data?.properties?.userDefined)
								// user defined property exists then image has a htt:// url
								// set that url as receiver profile img
								setAccountImage(data?.properties?.userDefined?.avatar);
							else if (data?.properties?.verified) {
								// if user has a verified property it would be an NFT
								// convert nft address to https url to fetch image
								// replace "ipfs://" -> "https://ipfs.io/ipfs/"
								setAccountImage(data?.properties?.verified?.avatar.replace("ipfs://", "https://ipfs.io/ipfs/"));
							}
						}
					});
				}
			}

			setRecoveredAccount(account);
		}
	};

	const AddNewAccount = async () => {
		dispatch(
			addNewAccountSecretKeyForUser(
				{
					"account-address": recoveredAccount.addr,
					"secret-key": recoveredAccount.sk,
				},
				notify
			)
		);

		dispatch(
			addNewAccountInUserProfile(
				{ "account-address": recoveredAccount.addr, name: accountName, "profile-image": accountImage },
				navigatePage,
				notify
			)
		);
	};

	const ClearAllStateAndValues = () => {
		setPasteFromClipBoardModalIsOpen(false);
		setEnterUsingKeyboardModalIsOpen(false);
		setMnemonic(null);
		setRecoveredAccount(null);
		setIsAccountVerified(false);
		setAccountName(null);
		setAccountImage(null);
		setIsWarningGiven(false);
	};

	return (
		<Transition
			as={Fragment}
			appear={true}
			show={true}
			enter="transition-opacity duration-500"
			enterFrom="opacity-0"
			enterTo="opacity-100"
			leave="transition-opacity duration-150"
			leaveFrom="opacity-100"
			leaveTo="opacity-0"
		>
			<div className="w-full space-x-4 items-center justify-center">
				<div className="items-center justify-center flex flex-col col-span-2 py-2 px-3 w-full space-y-4">
					<div className="py-1 px-2 items-center flex justify-start w-full">
						<div
							className={`flex items-center justify-center w-full text-2xl xl:text-3xl self-center font-bold ${themeColor?.text}`}
						>
							<h1 className="">{appLanguage?.importExistingAccount}</h1>
						</div>
					</div>

					<div className="w-[70%] items-center justify-center flex flex-col pb-1 px-3 space-y-5">
						{isAccountVerified ? (
							<>
								<div className="w-full flex flex-col space-y-4">
									<p className={`text-sm font-bold ${themeColor?.text}`}>{appLanguage?.almostThere}</p>
									<p className={`text-sm ${themeColor?.text}`}>{appLanguage?.toMakeItEasier}</p>
								</div>

								<div className="flex-col relative">
									{accountImage && accountImage ? (
										<img
											className={`w-20 h-20 object-contain object-center`}
											src={accountImage}
											alt="temp"
											loading="lazy"
										/>
									) : (
										<UserIconRoundIcon className="w-20 h-20" fill={themeColor?.hex} />
									)}
									<EditIcon
										className="w-5 absolute -bottom-1 -right-1 cursor-pointer bg-white rounded-md hover:shadow-lg"
										fill={themeColor?.hex}
										onClick={() => hiddenImageFileInput.current.click()}
									/>
									<input
										style={{ display: "none" }}
										ref={hiddenImageFileInput}
										type="file"
										accept="image/x-png,image/jpeg"
										onChange={(e) => handleFileRead(e)}
									/>
								</div>

								<input
									className={`w-full relative block px-3 py-2 mb-8 border rounded-md focus:outline-none focus:${themeColor?.ring} focus:${themeColor?.border}`}
									name="accountName"
									placeholder={appLanguage?.accountName}
									type="text"
									value={accountName ? accountName : ""}
									required
									onChange={(e) => setAccountName(e.target.value)}
								/>
								<div className="w-full flex items-center justify-center mt-9">
									{updatingData ? (
										<img className="animate-spin mx-auto h-5 w-auto" src="/media/logo.png" alt="img" />
									) : (
										<button
											onClick={() => AddNewAccount()}
											disabled={mnemonic && accountName ? false : true}
											className={`text-white ${themeColor?.bg} py-3 px-8 mt-5 rounded-md hover:shadow-lg disabled:bg-dark-blue disabled:bg-opacity-10 disabled:text-dark-blue disabled:shadow-none smooth`}
										>
											{appLanguage?.continue}
										</button>
									)}
								</div>
							</>
						) : (
							<>
								<div className="w-full flex items-center justify-center space-x-6 border-b-2 pb-3">
									<p className={`text-sm ${themeColor?.text}`}>{appLanguage?.enterYourSecretWords}</p>
								</div>

								<div className="w-full flex flex-col items-center justify-center space-y-5">
									<button
										className={`flex items-center px-6 py-4 rounded-lg text-white ${themeColor?.bg} space-x-3 hover:shadow-lg`}
										onClick={() => setPasteFromClipBoardModalIsOpen(true)}
									>
										<PasteIcon className={`w-4`} fill="#ffffff" />
										<p className="text-sm text-white">{appLanguage?.pasteFromClipboard}</p>
										<ArrowFrontIcon className={`w-4`} fill="#ffffff" />
									</button>

									<button
										className={`flex items-center px-6 py-4 rounded-lg text-white ${themeColor?.bg} space-x-3 hover:shadow-lg`}
										onClick={() => setEnterUsingKeyboardModalIsOpen(true)}
									>
										<KeyboardIcon className={`w-4`} fill="#ffffff" />
										<p className="text-sm text-white">{appLanguage?.enterUsingKeyboard}</p>
										<ArrowFrontIcon className={`w-4`} fill="#ffffff" />
									</button>
								</div>

								<div className="flex flex-col w-full items-center justify-center text-center space-y-3">
									<p
										className={`text-md font-medium ${themeColor?.text}`}
										dangerouslySetInnerHTML={{ __html: appLanguage?.makeSureYourSecretWords }}
									/>
								</div>

								{mnemonic && mnemonic.length && (
									<div className="flex flex-col w-full items-start justify-start space-y-1">
										<p className={`text-sm ${themeColor?.text}`}>{appLanguage?.secretWordsYouEnteredAre}</p>
										<div
											className={`w-full flex flex-wrap justify-between px-3 rounded-lg ${themeColor?.bg} bg-opacity-10`}
										>
											{mnemonic.map((mnemonic, index) => (
												<div className="flex w-[25%] items-start justify-start py-2" key={index}>
													<p className={`pr-2 text-xs ${themeColor?.text}`}>{index + 1}.</p>
													<p className={`pr-2 text-xs ${themeColor?.text}`}>{mnemonic}</p>
												</div>
											))}
										</div>
									</div>
								)}
							</>
						)}
					</div>
				</div>

				<PasteFromClipBoardModal
					isOpen={pasteFromClipBoardModalIsOpen}
					setIsOpen={setPasteFromClipBoardModalIsOpen}
					PasteFromClipboard={PasteFromClipboard}
					inputValue={mnemonic}
					setInputValue={setMnemonic}
					submit={RecoverAccount}
				/>

				<EnterUsingKeyboardModal
					isOpen={enterUsingKeyboardModalIsOpen}
					setIsOpen={setEnterUsingKeyboardModalIsOpen}
					inputValue={mnemonic}
					action={MnemonicToArray}
					submit={RecoverAccount}
				/>
			</div>
		</Transition>
	);
};
export default ImportTAccount;
